// Per-contract settings card (compliance target, performance thresholds,
// meeting on-time grace window) — the fourth card rendered by
// ManagedContractForm. Purely presentational: the parent form owns all
// state via useState (it needs the raw string values to build the submit
// payload) and passes it down as controlled value/onChange pairs, matching
// the pattern used for the form's other field-level components.

export type GraceMode = "default" | "single" | "by-freq";

export interface GraceByFrequency {
  daily: string;
  weekly: string;
  monthly: string;
  quarterly: string;
  annually: string;
  oneOff: string;
}

interface ContractSettingsCardProps {
  complianceTarget: string;
  onComplianceTargetChange: (value: string) => void;
  perfSuccessThreshold: string;
  onPerfSuccessThresholdChange: (value: string) => void;
  perfWatchThreshold: string;
  onPerfWatchThresholdChange: (value: string) => void;
  perfThresholdError: string | null;
  graceMode: GraceMode;
  onGraceModeChange: (mode: GraceMode) => void;
  meetingGraceDays: string;
  onMeetingGraceDaysChange: (value: string) => void;
  graceByFreq: GraceByFrequency;
  onGraceByFreqChange: (key: keyof GraceByFrequency, value: string) => void;
  disabled: boolean;
}

export default function ContractSettingsCard({
  complianceTarget,
  onComplianceTargetChange,
  perfSuccessThreshold,
  onPerfSuccessThresholdChange,
  perfWatchThreshold,
  onPerfWatchThresholdChange,
  perfThresholdError,
  graceMode,
  onGraceModeChange,
  meetingGraceDays,
  onMeetingGraceDaysChange,
  graceByFreq,
  onGraceByFreqChange,
  disabled,
}: ContractSettingsCardProps) {
  return (
    <div className="card p-6 space-y-4">
      <h2 className="text-h3 text-slate-900 dark:text-slate-50">
        Contract settings
      </h2>
      <p className="text-caption text-slate-500 dark:text-slate-400">
        Per-contract overrides for the dashboards and metrics. Leave blank
        to use the platform defaults.
      </p>

      <div>
        <label htmlFor="complianceTarget" className="label">
          Compliance target %
        </label>
        <input
          id="complianceTarget"
          type="number"
          min={1}
          max={100}
          step={1}
          inputMode="decimal"
          value={complianceTarget}
          onChange={(e) => onComplianceTargetChange(e.target.value)}
          className="input max-w-xs"
          disabled={disabled}
          placeholder="95"
        />
        <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
          Reference line on the KPI Overview compliance chart. Default
          95%.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="performanceSuccessThreshold" className="label">
            Performance success threshold %
          </label>
          <input
            id="performanceSuccessThreshold"
            type="number"
            min={1}
            max={100}
            step={1}
            inputMode="decimal"
            value={perfSuccessThreshold}
            onChange={(e) => onPerfSuccessThresholdChange(e.target.value)}
            className="input max-w-xs"
            disabled={disabled}
            placeholder="90"
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            Pillars at or above this score render emerald on the
            Performance gauge. Default 90%.
          </p>
        </div>
        <div>
          <label htmlFor="performanceWatchThreshold" className="label">
            Performance watch threshold %
          </label>
          <input
            id="performanceWatchThreshold"
            type="number"
            min={1}
            max={100}
            step={1}
            inputMode="decimal"
            value={perfWatchThreshold}
            onChange={(e) => onPerfWatchThresholdChange(e.target.value)}
            className="input max-w-xs"
            disabled={disabled}
            placeholder="70"
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            Below this score the pillar renders red (at risk).
            Between watch and success it renders amber. Default 70%.
          </p>
        </div>
      </div>
      {perfThresholdError && (
        <p
          className="text-caption text-red-600 dark:text-red-400"
          role="alert"
        >
          {perfThresholdError}
        </p>
      )}

      <fieldset>
        <legend className="label">Meeting on-time grace</legend>
        <p className="mt-1 mb-3 text-caption text-slate-500 dark:text-slate-400">
          How many days a meeting can slip past its scheduled date and
          still count as on time on the Held on time tile.
        </p>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-body-sm text-slate-700 dark:text-slate-300">
            <input
              type="radio"
              name="graceMode"
              value="default"
              checked={graceMode === "default"}
              onChange={() => onGraceModeChange("default")}
              disabled={disabled}
              className="mt-1 accent-brand-600"
            />
            <span>
              <span className="font-medium">
                Cadence-proportional default
              </span>
              <span className="block text-caption text-slate-500 dark:text-slate-400">
                weekly 2d / monthly 7d / quarterly &amp; annual 14d /
                one-off 7d
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-body-sm text-slate-700 dark:text-slate-300">
            <input
              type="radio"
              name="graceMode"
              value="single"
              checked={graceMode === "single"}
              onChange={() => onGraceModeChange("single")}
              disabled={disabled}
              className="mt-1 accent-brand-600"
            />
            <span className="flex-1">
              <span className="font-medium">
                Same window for every meeting
              </span>
              {graceMode === "single" && (
                <div className="mt-2 inline-flex items-center gap-2">
                  <input
                    id="meetingGraceDays"
                    type="number"
                    min={0}
                    max={60}
                    step={1}
                    inputMode="numeric"
                    value={meetingGraceDays}
                    onChange={(e) =>
                      onMeetingGraceDaysChange(e.target.value)
                    }
                    className="input w-24"
                    disabled={disabled}
                    placeholder="0"
                    aria-label="Meeting grace days (single value)"
                  />
                  <span className="text-caption text-slate-500 dark:text-slate-400">
                    days (0 = no grace)
                  </span>
                </div>
              )}
            </span>
          </label>

          <label className="flex items-start gap-2 text-body-sm text-slate-700 dark:text-slate-300">
            <input
              type="radio"
              name="graceMode"
              value="by-freq"
              checked={graceMode === "by-freq"}
              onChange={() => onGraceModeChange("by-freq")}
              disabled={disabled}
              className="mt-1 accent-brand-600"
            />
            <span className="flex-1">
              <span className="font-medium">Per meeting frequency</span>
              {graceMode === "by-freq" && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(
                    [
                      ["daily", "Daily"],
                      ["weekly", "Weekly"],
                      ["monthly", "Monthly"],
                      ["quarterly", "Quarterly"],
                      ["annually", "Annual"],
                      ["oneOff", "One-off"],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key}>
                      <label
                        htmlFor={`grace-${key}`}
                        className="text-caption text-slate-600 dark:text-slate-300"
                      >
                        {label}
                      </label>
                      <input
                        id={`grace-${key}`}
                        type="number"
                        min={0}
                        max={60}
                        step={1}
                        inputMode="numeric"
                        value={graceByFreq[key]}
                        onChange={(e) =>
                          onGraceByFreqChange(key, e.target.value)
                        }
                        className="input"
                        disabled={disabled}
                        placeholder="default"
                      />
                    </div>
                  ))}
                </div>
              )}
            </span>
          </label>
        </div>
      </fieldset>
    </div>
  );
}
